import React, { useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { BulkMessageDetailsContext } from "../BulkMessageDetails";
import { StudentBar } from "../StudentBar";
import { BulkMessage } from "@/__generated__/graphql";
import { Button, Card, CardContent } from "@/components/ui";
import { FilterDisplay } from "../Components/FilterDisplay";
import {
  buildFormFilters,
  buildMessageParts,
  buildSender,
  buildSendOption,
  studentsFromApi,
} from "../utils/apiHelpers";
import { ComposeDisplay } from "../Components/ComposeDisplay";
import { Pencil } from "lucide-react";
import { RouteId } from "@/routes/getRoute";
import { useProtectedNavigation } from "@/routes/useProtectedNavigation";
import { useOrgTimeZone } from "@/hooks/useOrgTimeZone";

export function CampaignDetails() {
  const { bulkMessage, isSent } = useOutletContext<BulkMessageDetailsContext>();
  const { timeZone } = useOrgTimeZone();

  const senderInfo = useMemo(() => {
    if (bulkMessage.sentFrom) {
      return {
        name: `${bulkMessage.sentFrom.firstName} ${bulkMessage.sentFrom.lastName}`.trim(),
        profilePhoto: bulkMessage.sentFrom.profilePhoto,
      };
    }
    return {
      name: undefined,
      profilePhoto: null,
    };
  }, [bulkMessage.sentFrom]);

  return (
    <Card>
      <CardContent className="flex flex-col p-0 px-6 pt-9">
        <StudentSummary bulkMessage={bulkMessage} isSent={isSent} />
        <div className="flex border-b py-6 pb-10">
          <div className="flex grow">
            <FilterDisplay
              filters={buildFormFilters(bulkMessage.filters || {})}
            />
          </div>
          <EditButton
            disabled={isSent}
            path="editBulkMessaging"
            params={{
              bulkMessageId: bulkMessage.id ?? "",
            }}
          />
        </div>
        <div className="flex pt-10 pb-0">
          <div className="flex grow">
            <ComposeDisplay
              sender={buildSender(
                bulkMessage.senderType,
                bulkMessage.sentFrom?.id,
              )}
              sendOption={buildSendOption(
                bulkMessage.startSendingAt,
                bulkMessage.stopSendingAt,
                { timeZone },
              )}
              messageParts={buildMessageParts(bulkMessage.messageParts)}
              senderName={senderInfo.name}
              senderProfilePhoto={senderInfo.profilePhoto}
              timeZone={timeZone ?? undefined}
            />
          </div>
          <EditButton
            disabled={isSent}
            path="editBulkMessagingCompose"
            params={{
              bulkMessageId: bulkMessage.id ?? "",
            }}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function StudentSummary({
  bulkMessage,
  isSent,
}: {
  bulkMessage: Partial<BulkMessage>;
  isSent: boolean;
}) {
  const students = studentsFromApi(bulkMessage.sentTo);

  const totalCount = bulkMessage.sentTo?.totalCount;

  return (
    <div className="flex flex-col gap-6 border-b pb-6">
      <p className="text-sm text-foreground">
        {isSent ? (
          <Trans
            i18nKey="bulkMessaging.details.foundStudentsCompleted"
            count={totalCount}
          >
            This campaign was sent to
            <strong className="font-medium">{totalCount}</strong> students.
          </Trans>
        ) : (
          <Trans
            i18nKey="bulkMessaging.details.foundStudentsActive"
            count={totalCount}
          >
            This campaign has been sent to
            <strong className="font-medium">{totalCount}</strong> students.
          </Trans>
        )}
      </p>
      <StudentBar
        students={students}
        totalCount={totalCount}
        header={false}
        emptyHeader={false}
      />
    </div>
  );
}

function EditButton({
  path,
  params,
  disabled,
}: {
  path: RouteId;
  params: Record<string, string>;
  disabled: boolean;
}) {
  const { t } = useTranslation("translation", {
    keyPrefix: "bulkMessaging.review",
  });
  const { navToRoute } = useProtectedNavigation();

  return (
    <Button
      disabled={disabled}
      type="button"
      onClick={() => navToRoute(path, params)}
      variant="outline"
    >
      <Pencil />
      {t("edit")}
    </Button>
  );
}
